import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Theme exploration sandbox. Renders the same Next Exam hero card in
 * eight distinct modern dashboard design languages so we can pick a
 * direction with eyes, not adjectives.
 *
 * Not linked from nav — visit /themes manually.
 */
export const Route = createFileRoute("/_app/themes")({
  component: ThemesPage,
});

interface MockExam {
  name: string;
  countdown: string;
  date: string;
  maxMarks: number;
  durationHr: number;
  subjects: ["PHY", "CHE", "BIO"];
}

const MOCK: MockExam = {
  name: "NEET Mock 7",
  countdown: "4d 12h",
  date: "Jun 14",
  maxMarks: 720,
  durationHr: 3,
  subjects: ["PHY", "CHE", "BIO"],
};

function ThemesPage() {
  useDocumentTitle("Themes");

  return (
    <div className="flex flex-col gap-8">
      <header className="flex max-w-3xl flex-col gap-3">
        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Theme exploration
        </span>
        <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">Pick a direction.</h1>
        <p className="text-sm text-muted-foreground">
          Each tile renders the same "Next Exam" hero in a distinct modern dashboard style. Inspect
          them at the size they'd live in your dashboard, then tell me which to commit. Captioned
          inspirations show the real-world product the look mimics.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ThemeFrame
          name="Bento minimalism"
          tag="Current"
          inspiration="Apple newsroom · Notion home"
          summary="Calm tiles, breathable padding, restrained accent. Reads as serious EdTech, not as a developer tool."
        >
          <BentoExam />
        </ThemeFrame>

        <ThemeFrame
          name="Linear / Vercel premium dark"
          tag="Dark"
          inspiration="Linear · Vercel · Raycast"
          summary="Deep neutrals, frosted glass cards, subtle indigo glow on hover. Quiet enough for daily use, distinctly premium."
        >
          <LinearExam />
        </ThemeFrame>

        <ThemeFrame
          name="Aurora glass"
          tag="Dark · Maximal"
          inspiration="Arc browser · Apple Vision Pro"
          summary="Multi-color aurora bleed behind heavy glassmorphism. Distinctive but risks the vivid-AI-gradient look you wanted to avoid."
        >
          <AuroraExam />
        </ThemeFrame>

        <ThemeFrame
          name="Cyan neon synthwave"
          tag="Dark · Punchy"
          inspiration="Cursor · Replit Ghost · 80s sci-fi"
          summary="Pure black, single cyan accent, grid background, glowing borders. Striking but can be fatiguing over long study sessions."
        >
          <NeonExam />
        </ThemeFrame>

        <ThemeFrame
          name="Cyberpunk grid"
          tag="Dark · Aggressive"
          inspiration="TRON · Cyberpunk 2077 UI · Blade Runner"
          summary="High contrast magenta/cyan, scanlines, hard-edge corners. Maximum drama. Probably too loud for an LMS but here for contrast."
        >
          <CyberpunkExam />
        </ThemeFrame>

        <ThemeFrame
          name="Stripe / Apple minimal"
          tag="Light"
          inspiration="Stripe Dashboard · Apple App Store Connect"
          summary="Clean white surfaces, soft shadows, indigo used sparingly. The professional default. Reliable, never offends."
        >
          <StripeExam />
        </ThemeFrame>

        <ThemeFrame
          name="Notion monochrome editorial"
          tag="Light · Type-driven"
          inspiration="Notion · Things · Bear app"
          summary="Pure grayscale, type hierarchy as the only signal. No accent color anywhere. Focused, content-first, almost paper-like."
        >
          <NotionExam />
        </ThemeFrame>

        <ThemeFrame
          name="Material You soft 3D"
          tag="Light · Playful"
          inspiration="Android 13+ · Google Calendar"
          summary="Soft pastel surfaces, large radii, generous shadows, color-extracted accents. Friendly. Good for younger students."
        >
          <MaterialYouExam />
        </ThemeFrame>
      </div>

      <footer className="rounded-2xl border border-dashed border-border bg-card/40 p-5 text-sm text-muted-foreground">
        <strong className="font-medium text-foreground">Tell me which one</strong> — I'll promote
        the chosen style into the design tokens (colors, shadows, radii, type) and re-render the
        Dashboard + Student Detail pages in it. Roughly a 20-minute pass per page.
      </footer>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Frame wrapper — neutral chrome so the sample card inside is the focus.       */
/* ─────────────────────────────────────────────────────────────────────────── */

function ThemeFrame({
  name,
  tag,
  inspiration,
  summary,
  children,
}: {
  name: string;
  tag: string;
  inspiration: string;
  summary: string;
  children: ReactNode;
}) {
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-border/60 px-5 py-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="text-sm font-semibold tracking-tight text-foreground">{name}</h2>
          <p className="text-[11px] text-muted-foreground">{inspiration}</p>
        </div>
        <span className="shrink-0 rounded-full border border-border/60 bg-muted/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {tag}
        </span>
      </div>
      <div className="relative isolate">{children}</div>
      <p className="border-t border-border/60 px-5 py-3 text-xs text-muted-foreground">{summary}</p>
    </article>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 1 — Bento minimalism (current)                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

function BentoExam() {
  return (
    <div className="bg-background p-6">
      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-[var(--shadow-bento)]">
        <header className="mb-3 flex items-start justify-between gap-4">
          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Next exam
          </span>
          <span className="tabular rounded-full border border-border/60 bg-background/60 px-3 py-1 text-[11px] font-medium text-foreground">
            in {MOCK.countdown}
          </span>
        </header>
        <h3 className="text-2xl font-semibold leading-tight tracking-tight text-foreground md:text-3xl">
          {MOCK.name}
        </h3>
        <p className="tabular mt-2 text-sm text-muted-foreground">
          {MOCK.date} · {MOCK.maxMarks} marks · {MOCK.durationHr} hr
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {MOCK.subjects.map((s) => (
            <span
              key={s}
              className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 2 — Linear / Vercel premium dark                                     */
/* ─────────────────────────────────────────────────────────────────────────── */

function LinearExam() {
  return (
    <div className="bg-[#0a0b0f] p-6 text-zinc-200">
      <div
        className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.02] p-5 backdrop-blur-xl transition-shadow duration-200"
        style={{
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.04) inset, 0 20px 40px -24px rgba(99,102,241,0.25)",
        }}
      >
        <header className="mb-3 flex items-start justify-between gap-4">
          <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-zinc-500">
            Next exam
          </span>
          <span className="tabular rounded-full border border-indigo-400/30 bg-indigo-500/10 px-2.5 py-0.5 text-[10px] font-medium text-indigo-300">
            in {MOCK.countdown}
          </span>
        </header>
        <h3 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
          {MOCK.name}
        </h3>
        <p className="tabular mt-2 text-sm text-zinc-400">
          {MOCK.date} · {MOCK.maxMarks} marks · {MOCK.durationHr} hr
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {MOCK.subjects.map((s) => (
            <span
              key={s}
              className="rounded-md border border-white/[0.06] bg-white/[0.03] px-2 py-0.5 text-[10px] font-medium tracking-wider text-zinc-400"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 3 — Aurora glass                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */

function AuroraExam() {
  return (
    <div
      className="relative overflow-hidden p-6"
      style={{
        backgroundColor: "#06060a",
        backgroundImage:
          "radial-gradient(circle at 15% 20%, rgba(99,102,241,0.35), transparent 45%), radial-gradient(circle at 85% 80%, rgba(34,211,238,0.28), transparent 45%), radial-gradient(circle at 50% 50%, rgba(217,70,239,0.18), transparent 60%)",
      }}
    >
      <div
        className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-2xl"
        style={{
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.05) inset, 0 30px 60px -20px rgba(99,102,241,0.4), 0 0 80px -20px rgba(34,211,238,0.25)",
        }}
      >
        <header className="mb-3 flex items-start justify-between gap-4">
          <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-white/60">
            Next exam
          </span>
          <span className="tabular rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-md">
            in {MOCK.countdown}
          </span>
        </header>
        <h3 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
          {MOCK.name}
        </h3>
        <p className="tabular mt-2 text-sm text-white/70">
          {MOCK.date} · {MOCK.maxMarks} marks · {MOCK.durationHr} hr
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {MOCK.subjects.map((s) => (
            <span
              key={s}
              className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium tracking-wider text-white/80 backdrop-blur-md"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 4 — Cyan neon synthwave                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

function NeonExam() {
  return (
    <div
      className="relative overflow-hidden p-6"
      style={{
        backgroundColor: "#000",
        backgroundImage:
          "linear-gradient(rgba(34,211,238,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.06) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
    >
      <div
        className="relative rounded-lg border border-cyan-400/40 bg-black/60 p-5 backdrop-blur-sm"
        style={{
          boxShadow:
            "0 0 0 1px rgba(34,211,238,0.15), 0 0 40px -8px rgba(34,211,238,0.45), inset 0 0 20px rgba(34,211,238,0.05)",
        }}
      >
        <header className="mb-3 flex items-start justify-between gap-4">
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-cyan-400">
            &gt;&gt; Next exam
          </span>
          <span className="tabular rounded-sm border border-cyan-400/60 bg-cyan-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-cyan-300">
            T-{MOCK.countdown.replace(" ", "")}
          </span>
        </header>
        <h3 className="text-2xl font-bold uppercase tracking-tight text-cyan-100 md:text-3xl">
          {MOCK.name}
        </h3>
        <p className="tabular mt-2 font-mono text-xs uppercase text-cyan-400/70">
          {MOCK.date} · {MOCK.maxMarks}MARKS · {MOCK.durationHr}H
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {MOCK.subjects.map((s) => (
            <span
              key={s}
              className="rounded-sm border border-cyan-400/30 bg-cyan-400/5 px-2 py-0.5 font-mono text-[10px] font-bold tracking-widest text-cyan-300"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 5 — Cyberpunk grid                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

function CyberpunkExam() {
  return (
    <div
      className="relative overflow-hidden p-6"
      style={{
        backgroundColor: "#0a000d",
        backgroundImage:
          "repeating-linear-gradient(0deg, rgba(255,0,128,0.04) 0px, rgba(255,0,128,0.04) 1px, transparent 1px, transparent 4px)",
      }}
    >
      <div
        className="relative border-2 border-fuchsia-500/70 bg-black/80 p-5"
        style={{
          clipPath:
            "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))",
          boxShadow:
            "0 0 0 1px rgba(255,0,128,0.4), 0 0 40px -4px rgba(255,0,128,0.5), 0 0 20px -4px rgba(34,211,238,0.4)",
        }}
      >
        <header className="mb-3 flex items-start justify-between gap-4">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-fuchsia-400">
            ▸ NEXT EXAM
          </span>
          <span className="tabular border border-fuchsia-400/80 bg-fuchsia-500/20 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-fuchsia-200">
            {MOCK.countdown}
          </span>
        </header>
        <h3 className="font-mono text-2xl font-bold uppercase tracking-tight text-cyan-300 md:text-3xl">
          {MOCK.name}
        </h3>
        <p className="tabular mt-2 font-mono text-xs uppercase tracking-wider text-fuchsia-300/70">
          {MOCK.date} | {MOCK.maxMarks}MK | {MOCK.durationHr}H
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          {MOCK.subjects.map((s, i) => (
            <span
              key={s}
              className={`border px-2 py-0.5 font-mono text-[10px] font-bold tracking-widest ${
                i === 0
                  ? "border-cyan-400 bg-cyan-500/20 text-cyan-200"
                  : i === 1
                    ? "border-fuchsia-400 bg-fuchsia-500/20 text-fuchsia-200"
                    : "border-yellow-400 bg-yellow-500/20 text-yellow-200"
              }`}
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 6 — Stripe / Apple minimal (light)                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

function StripeExam() {
  return (
    <div className="bg-[#f6f8fb] p-6">
      <div
        className="rounded-2xl border border-zinc-200/80 bg-white p-6"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.06)" }}
      >
        <header className="mb-4 flex items-start justify-between gap-4">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Next exam
          </span>
          <span className="tabular rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-0.5 text-[11px] font-medium text-zinc-700">
            in {MOCK.countdown}
          </span>
        </header>
        <h3 className="text-2xl font-semibold tracking-tight text-zinc-900 md:text-3xl">
          {MOCK.name}
        </h3>
        <p className="tabular mt-2 text-sm text-zinc-600">
          {MOCK.date} · {MOCK.maxMarks} marks · {MOCK.durationHr} hr
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {MOCK.subjects.map((s) => (
            <span
              key={s}
              className="rounded-md bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-indigo-700"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 7 — Notion monochrome editorial                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

function NotionExam() {
  return (
    <div className="bg-white p-6 text-zinc-900">
      <div className="rounded-md border border-zinc-200 bg-white p-6">
        <header className="mb-3 flex items-start justify-between gap-4">
          <span className="text-[11px] font-medium text-zinc-500">Next exam</span>
          <span className="tabular text-[11px] text-zinc-500">in {MOCK.countdown}</span>
        </header>
        <h3 className="text-2xl font-semibold tracking-tight text-zinc-900 md:text-3xl">
          {MOCK.name}
        </h3>
        <p className="tabular mt-2 text-sm text-zinc-600">
          {MOCK.date} · {MOCK.maxMarks} marks · {MOCK.durationHr} hr
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1">
          {MOCK.subjects.map((s, i) => (
            <span key={s} className="contents">
              {i > 0 && <span className="text-zinc-300">·</span>}
              <span className="text-xs text-zinc-500">{s}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Sample 8 — Material You soft 3D                                             */
/* ─────────────────────────────────────────────────────────────────────────── */

function MaterialYouExam() {
  return (
    <div className="bg-[#fef3f0] p-6">
      <div
        className="rounded-[28px] bg-white p-6"
        style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 10px 28px -8px rgba(244,114,182,0.18)" }}
      >
        <header className="mb-4 flex items-start justify-between gap-4">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-500">
            Next exam
          </span>
          <span
            className="tabular rounded-full bg-rose-100 px-3 py-1 text-[11px] font-semibold text-rose-700"
            style={{ boxShadow: "0 1px 2px rgba(244,114,182,0.2) inset" }}
          >
            in {MOCK.countdown}
          </span>
        </header>
        <h3 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">{MOCK.name}</h3>
        <p className="tabular mt-2 text-sm text-zinc-600">
          {MOCK.date} · {MOCK.maxMarks} marks · {MOCK.durationHr} hr
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {MOCK.subjects.map((s) => (
            <span
              key={s}
              className="rounded-full bg-rose-100 px-3 py-1 text-[10px] font-bold tracking-wider text-rose-700"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
